import {Shield, Zap, Users, Award, ChevronLeft, ChevronRight} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

const values = [
{
  icon: Shield,
  title: "Qualidade",
  desc: "Produtos desenvolvidos para garantir segurança e alta durabilidade nas aplicações industriais."
},
{
  icon: Zap,
  title: "Agilidade",
  desc: "Estoque estratégico e atendimento rápido para não parar a sua produção."
},
{
  icon: Users,
  title: "Atendimento Técnico",
  desc: "Equipe especializada para orientar a melhor solução para cada necessidade."
},
{
  icon: Award,
  title: "Excelência",
  desc: "Compromisso com a confiança, transparência e eficiência operacional."
}];


const aboutImages = [
  {
    src: "https://static.lumi.new/98/98d560f2bffa0d8569321a907c9e45e8.png",
    alt: "Estrutura Manguenela Rio – Workshop Industrial"
  },
  {
    src: "https://static.lumi.new/5b/5bb3e651c414ab18c3bf79b9c613038a.webp",
    alt: "Fachada Manguenela Rio"
  },
  {
    src: "https://static.lumi.new/55/55b4f4b988f76ee41c4acb0642df806d.webp",
    alt: "Departamento Comercial Manguenela Rio"
  }
];

export default function About() {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % aboutImages.length);
    }, 5000);
  };

  useEffect(() => {
    startTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const goTo = (idx: number) => {
    setCurrent(idx);
    startTimer();
  };

  const prev = () => goTo((current - 1 + aboutImages.length) % aboutImages.length);
  const next = () => goTo((current + 1) % aboutImages.length);

  return (
    <section id="empresa" className="bg-white py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* ── Section header ── */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <div>
            <span className="inline-flex items-center gap-3 text-[#F5A623] font-black uppercase tracking-[0.25em] text-xs mb-4">
              <span className="w-10 h-px bg-[#F5A623]" />
              Quem Somos
            </span>
            <h2 className="font-barlow-condensed text-5xl md:text-7xl font-black text-[#0D1B3E] uppercase leading-[0.9]">
              A Empresa
            </h2>
            <p className="mt-4 text-gray-500 text-lg max-w-lg leading-relaxed font-light">
              Fornecimento industrial com qualidade, velocidade e suporte técnico de ponta.
            </p>
          </div>
          <div className="h-px flex-1 max-w-xs bg-gradient-to-r from-[#F5A623]/60 to-transparent hidden md:block mb-4" />
        </div>

        {/* ── Two-column grid ── */}
        <div className="grid lg:grid-cols-2 gap-14 items-center">

          {/* Image block – slider */}
          <div className="relative group">
            {/* Decorative corner frames */}
            <div className="absolute -top-4 -left-4 w-20 h-20 border-l-2 border-t-2 border-[#F5A623]/50 rounded-tl-2xl z-20" />
            <div className="absolute -bottom-4 -right-4 w-20 h-20 border-r-2 border-b-2 border-[#F5A623]/30 rounded-br-2xl z-20" />

            {/* Dark fill block for depth */}
            <div className="absolute -bottom-6 -right-6 w-full h-full bg-[#0D1B3E]/8 rounded-3xl -z-10" />

            <div className="rounded-2xl overflow-hidden shadow-[0_40px_80px_-20px_rgba(13,27,62,0.35)] relative">
              {aboutImages.map((img, i) => (
                <img
                  key={i}
                  src={img.src}
                  alt={img.alt}
                  className={`w-full h-72 md:h-[30rem] object-cover absolute inset-0 transition-opacity duration-700 ${
                    i === current ? 'opacity-100 relative' : 'opacity-0'
                  }`}
                  style={i !== current ? { position: 'absolute' } : {}}
                />
              ))}
            </div>

            {/* Prev/Next arrows */}
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 bg-[#0D1B3E]/70 hover:bg-[#F5A623] text-white hover:text-[#0D1B3E] rounded-full flex items-center justify-center transition-all duration-200 shadow-lg backdrop-blur-sm opacity-0 group-hover:opacity-100"
              aria-label="Anterior"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 bg-[#0D1B3E]/70 hover:bg-[#F5A623] text-white hover:text-[#0D1B3E] rounded-full flex items-center justify-center transition-all duration-200 shadow-lg backdrop-blur-sm opacity-0 group-hover:opacity-100"
              aria-label="Próxima"
            >
              <ChevronRight size={18} />
            </button>

            {/* Dot indicators */}
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 flex gap-2">
              {aboutImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`transition-all duration-300 rounded-full ${
                    i === current
                      ? 'w-6 h-2 bg-[#F5A623]'
                      : 'w-2 h-2 bg-white/50 hover:bg-white'
                  }`}
                  aria-label={`Imagem ${i + 1}`}
                />
              ))}
            </div>

            {/* Badge overlay */}
            <div className="absolute bottom-6 left-6 bg-[#0D1B3E] rounded-xl px-5 py-3 shadow-2xl border border-[#F5A623]/20 z-10">
              <p className="text-[#F5A623] font-black text-2xl font-barlow-condensed leading-none">+10</p>
              <p className="text-gray-300 text-xs uppercase tracking-widest mt-0.5 font-semibold">Anos de mercado</p>
            </div>
          </div>

          {/* Text block */}
          <div className="flex flex-col gap-7">
            <p className="text-gray-700 text-lg leading-relaxed text-justify">
              Somos uma empresa especializada no fornecimento de{" "}
              <strong className="text-[#0D1B3E] font-bold">
                mangueiras hidráulicas, conexões, terminais, correias e soluções industriais
              </strong>{" "}
              para os mais diversos segmentos do mercado.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed text-justify">Atuamos com foco em qualidade, agilidade e atendimento técnico especializado,
              oferecendo produtos desenvolvidos para garantir segurança, desempenho e alta
              durabilidade nas aplicações industriais, automotivas e de construção.

            </p>
            <p className="text-gray-600 text-lg leading-relaxed text-justify">Nosso compromisso é entregar soluções eficientes para condução hidráulica e
              industrial, unindo produtos de alto desempenho, estoque estratégico e
              atendimento próximo ao cliente.

            </p>

            {/* Inline stats row */}
            <div className="flex gap-8 pt-4 border-t border-gray-100">
              {[
              { n: "+500", l: "Produtos" },
              { n: "100%", l: "Qualidade" },
              { n: "RJ", l: "São Cristóvão" }].
              map(({ n, l }) =>
              <div key={l} className="flex flex-col">
                  <span className="font-barlow-condensed text-3xl font-black text-[#0D1B3E]">{n}</span>
                  <span className="text-xs text-gray-400 uppercase tracking-widest mt-0.5 font-semibold">{l}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Values grid ── */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-20">
          {values.map(({ icon: Icon, title, desc }) =>
          <div
            key={title}
            className="card-lift relative bg-white border border-gray-100 rounded-2xl p-7 hover:shadow-[0_24px_48px_-16px_rgba(13,27,62,0.22)] hover:border-[#F5A623]/30 group">

              {/* Top accent */}
              <div className="absolute top-0 left-7 right-7 h-px bg-gradient-to-r from-transparent via-[#F5A623]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="w-14 h-14 bg-gradient-to-br from-[#F5A623] to-[#d4890e] rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-[#F5A623]/25">
                <Icon size={24} className="text-[#0D1B3E]" />
              </div>
              <h3 className="text-[#0D1B3E] font-bold text-base mb-2 tracking-tight">{title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
            </div>
          )}
        </div>
      </div>
    </section>);

}